import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Wrench, Search, FileText, CheckCircle, Clock, AlertCircle, Calendar, Phone, DollarSign, Download, ArrowRight, X, Smartphone, ShieldCheck, Tag, Sparkles, Building2, User, Mail, ShieldAlert } from 'lucide-react';
import api from '../../utils/api';
import QRCode from 'qrcode';
import RazorpayModal from '../../components/RazorpayModal';
import PayPalModal from '../../components/PayPalModal';
import toast from 'react-hot-toast';

const Login = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Registration States
  const [isRegistering, setIsRegistering] = useState(false);
  const [regStep, setRegStep] = useState(1); // 1: Info, 2: Plan & Auth
  const [regData, setRegData] = useState({
    name: '',
    shopName: '',
    phone: '',
    email: '',
    username: '',
    password: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    website: '',
    whatsapp: '',
    gst: '',
    documentUploaded: '',
    plan: 'Starter',
    planId: null,      // DB plan ID
    planData: null     // full plan object for price/cycle display
  });
  const [apiPlans, setApiPlans] = useState([]);
  const [userExistsError, setUserExistsError] = useState('');
  const [isRazorpayOpen, setIsRazorpayOpen] = useState(false);
  const [isPayPalOpen, setIsPayPalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' | 'paypal'

  // Background wake-up ping on mount (wakes up sleeping backend server instantly in background)
  useEffect(() => {
    api.get('/api/health', { timeout: 4000 }).catch(() => {});
  }, []);

  // Fetch available plans from API on mount
  useEffect(() => {
    api.get('/api/saas/plans')
      .then(response => {
        const data = response.data;
        if (data.success && data.data.length > 0) {
          setApiPlans(data.data);
          // Auto-select first plan
          const first = data.data[0];
          setRegData(prev => ({ ...prev, plan: first.name, planId: first.id, planData: first }));
        }
      })
      .catch(e => console.error('Failed to load plans:', e));
  }, []);

  // Job Tracking states
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackPhone, setTrackPhone] = useState('');
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackedJobs, setTrackedJobs] = useState(null);
  const [trackError, setTrackError] = useState('');
  const [selectedInvoiceJob, setSelectedInvoiceJob] = useState(null);
  const [loginQrCodeUrl, setLoginQrCodeUrl] = useState('');

  useEffect(() => {
    if (selectedInvoiceJob?.customer) {
      const data = selectedInvoiceJob.customer.phone
        ? `${window.location.origin}/login?phone=${encodeURIComponent(selectedInvoiceJob.customer.phone)}`
        : `${window.location.origin}/login`;
      QRCode.toDataURL(data, { width: 150, margin: 1 })
        .then(url => setLoginQrCodeUrl(url))
        .catch(err => console.error('Error generating login QR code', err));
    }
  }, [selectedInvoiceJob]);

  // Auto-run tracking if phone query parameter is present in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phoneParam = params.get('phone') || params.get('trackPhone');
    if (phoneParam) {
      const trimmedPhone = phoneParam.trim();
      setTrackPhone(trimmedPhone);
      setShowTrackModal(true);
      setTrackingLoading(true);
      setTrackError('');
      setTrackedJobs(null);

      api.get(`/api/jobs/public/track?phone=${encodeURIComponent(trimmedPhone)}`)
        .then(response => {
          if (response.data?.success) {
            setTrackedJobs(response.data.data);
            if (response.data.data.length === 0) {
              setTrackError('No jobs found associated with this phone number.');
            }
          } else {
            setTrackError('Could not retrieve tracking details.');
          }
        })
        .catch(err => {
          console.error(err);
          setTrackError('Network error. Failed to connect to the tracking server.');
        })
        .finally(() => {
          setTrackingLoading(false);
        });
    }
  }, []);

  // Handle credentials change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle registration data change
  const handleRegChange = (e) => {
    const { name, value } = e.target;
    setRegData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when typing username or email
    if (name === 'username' || name === 'email') {
      setUserExistsError('');
    }
  };

  // Live Uniqueness Validation for Username and Email
  useEffect(() => {
    if (!isRegistering) return;

    const validateUniqueness = async () => {
      const usernameLower = regData.username.toLowerCase().trim();
      const emailLower = regData.email.toLowerCase().trim();

      if (!usernameLower && !emailLower) {
        setUserExistsError('');
        return;
      }

      try {
        const response = await api.post('/api/saas/clients/check-uniqueness', {
          username: usernameLower,
          email: emailLower
        });
        const data = response.data;
        if (data.success && !data.available) {
          if (data.takenField === 'username') {
            setUserExistsError(`Username "${regData.username}" is already taken!`);
          } else if (data.takenField === 'email') {
            setUserExistsError(`Business Email "${regData.email}" is already registered!`);
          }
          return;
        }
      } catch (err) {
        // Skip validation check on errors to avoid blocking the user
        setUserExistsError('');
      }

      setUserExistsError('');
    };

    const delayDebounce = setTimeout(() => {
      validateUniqueness();
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [regData.username, regData.email, isRegistering]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await login(formData);
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initiate Registration Payment
  const handleRegSubmit = (e) => {
    e.preventDefault();
    if (userExistsError) {
      toast.error('Please resolve duplicate username or email details');
      return;
    }
    if (!regData.name || !regData.shopName || !regData.phone || !regData.email || !regData.username || !regData.password) {
      toast.error('Please fill in all registration fields');
      return;
    }

    if (paymentMethod === 'paypal') {
      setIsPayPalOpen(true);
    } else {
      setIsRazorpayOpen(true);
    }
  };

  // Callback on successful simulated Razorpay checkout — register via API
  const handlePaymentSuccess = async (utr) => {
    try {
      const response = await api.post('/api/saas/clients', {
        name: regData.name,
        shopName: regData.shopName,
        phone: regData.phone,
        email: regData.email,
        username: regData.username.toLowerCase().trim(),
        password: regData.password,
        planId: regData.planId || null,
        address: regData.address,
        city: regData.city,
        state: regData.state,
        pincode: regData.pincode,
        website: regData.website,
        whatsapp: regData.whatsapp,
        gst: regData.gst,
        utr: utr || null,
      });
      const data = response.data;
      if (!data.success) {
        toast.error(data.message || 'Registration failed');
        return;
      }

      toast.success('Payment & Registration Successful! Logging you in...');

      // Auto login immediately
      await login({
        username: regData.username.toLowerCase().trim(),
        password: regData.password
      });
    } catch (err) {
      console.error('Registration API error:', err);
      const message = err.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
    }
  };

  const getPlanPriceINR = () => {
    // regData.planData is set when the user picks a plan from the API-fetched list
    if (regData.planData?.price !== undefined) return Number(regData.planData.price);
    if (regData.plan === 'Enterprise') return 249;
    if (regData.plan === 'Growth') return 99;
    return 49;
  };

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    if (!trackPhone.trim()) {
      setTrackError('Please enter your phone number');
      return;
    }

    setTrackingLoading(true);
    setTrackError('');
    setTrackedJobs(null);

    try {
      const response = await api.get(`/api/jobs/public/track?phone=${encodeURIComponent(trackPhone.trim())}`);
      if (response.data?.success) {
        setTrackedJobs(response.data.data);
        if (response.data.data.length === 0) {
          setTrackError('No jobs found associated with this phone number.');
        }
      } else {
        setTrackError('Could not retrieve tracking details.');
      }
    } catch (err) {
      console.error(err);
      setTrackError('Network error. Failed to connect to the tracking server.');
    } finally {
      setTrackingLoading(false);
    }
  };

  const statusColors = {
    PENDING: { bg: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', text: 'Pending Diagnosis' },
    IN_PROGRESS: { bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500', text: 'In Progress' },
    COMPLETED: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', text: 'Completed / Ready' },
    DELIVERED: { bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500', text: 'Delivered' },
    RETURNED: { bg: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-500', text: 'Returned' },
    REENTRY: { bg: 'bg-pink-50 text-pink-700 border-pink-200', dot: 'bg-pink-500', text: 'Re-entry / Under Warranty' }
  };

  const getStatusBadge = (status) => {
    const config = statusColors[status] || { bg: 'bg-gray-50 text-gray-700 border-gray-200', dot: 'bg-gray-500', text: status };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${config.bg}`}>
        <span className={`w-2 h-2 rounded-full mr-2 ${config.dot} animate-pulse`}></span>
        {config.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8FAFC] to-blue-50/50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative Blur Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-blue-200 opacity-20 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[45vw] h-[45vw] rounded-full bg-indigo-200 opacity-20 blur-[120px] pointer-events-none"></div>

      <div className="max-w-md w-full space-y-6 relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20 transform hover:scale-105 transition-all">
            <Wrench className="w-8 h-8 text-white" />
          </div>
          <h2 className="mt-5 text-3xl font-extrabold text-slate-900 tracking-tight">
            {isRegistering ? 'Register Your Repair Shop' : 'Repair Shop Management'}
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-semibold">
            {isRegistering ? 'Setup your premium multi-tenant subscription' : 'Sign in to your account to continue'}
          </p>
        </div>

        {/* 1. LOGIN SCREEN CONTAINER */}
        {!isRegistering ? (
          <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 transform transition-all">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Username Field */}
              <div>
                <label htmlFor="username" className="form-label font-semibold text-slate-700 mb-1.5 block text-xs uppercase tracking-wider">
                  Username or Email
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className={`input-field rounded-xl ${errors.username ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'}`}
                  placeholder="Enter your username or email"
                  value={formData.username}
                  onChange={handleChange}
                />
                {errors.username && (
                  <p className="form-error mt-1 text-xs text-red-500">{errors.username}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="form-label font-semibold text-slate-700 mb-1.5 block text-xs uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className={`input-field rounded-xl pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'}`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="form-error mt-1 text-xs text-red-500">{errors.password}</p>
                )}
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 flex items-center justify-center font-bold text-white shadow-lg shadow-blue-600/20 transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150 border border-blue-500/10"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2 border-white"></div>
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>
            </form>

            {/* Registration Trigger Link */}
            <div className="mt-5 text-center text-xs">
              <span className="text-slate-500 font-semibold">Want to launch a new Repair Shop? </span>
              <button
                type="button"
                onClick={() => {
                  setIsRegistering(true);
                  setRegStep(1);
                }}
                className="text-blue-600 hover:text-blue-700 font-bold underline transition-colors"
              >
                Register & Subscribe Now
              </button>
            </div>

            {/* Public Tracking Trigger Button */}
            <div className="mt-5 pt-5 border-t border-blue-100">
              <button
                onClick={() => {
                  setShowTrackModal(true);
                  setTrackPhone('');
                  setTrackedJobs(null);
                  setTrackError('');
                }}
                className="w-full py-3.5 px-4 bg-blue-50 hover:bg-blue-100/80 text-blue-700 border border-blue-200 hover:border-blue-350 font-bold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm shadow-blue-100"
              >
                <Smartphone className="w-5 h-5 animate-pulse" />
                <span>Track Repair Status</span>
              </button>
            </div>


          </div>
        ) : (
          /* 2. REGISTRATION WORKSPACE CONTAINER */
          <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 transform transition-all animate-fadeIn">

            {/* Steps indicator */}
            <div className="flex items-center justify-center gap-2 mb-6 border-b border-blue-50 pb-4">
              <div className={`h-2 rounded-full transition-all duration-300 ${regStep === 1 ? 'w-8 bg-blue-600' : 'w-2 bg-emerald-500'}`} />
              <div className={`h-2 rounded-full transition-all duration-300 ${regStep === 2 ? 'w-8 bg-blue-600' : 'w-2 bg-gray-200'}`} />
            </div>

            <form onSubmit={regStep === 1 ? (e) => { e.preventDefault(); setRegStep(2); } : handleRegSubmit} className="space-y-4">

              {/* STEP 1: OWNER & SHOP INFORMATION */}
              {regStep === 1 && (
                <div className="space-y-4 animate-fadeIn text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Owner Full Name *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          required
                          value={regData.name}
                          onChange={handleRegChange}
                          placeholder="e.g. Jeevaraajan"
                          className="w-full bg-white border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <User className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Company Name *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="shopName"
                          required
                          value={regData.shopName}
                          onChange={handleRegChange}
                          placeholder="e.g. Repair Shop"
                          className="w-full bg-white border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Building2 className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Phone *</label>
                      <div className="relative">
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={regData.phone}
                          onChange={handleRegChange}
                          placeholder="e.g. 08056339539"
                          className="w-full bg-white border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email *</label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          required
                          value={regData.email}
                          onChange={handleRegChange}
                          placeholder="e.g. jeevaraajan627@gmail.com"
                          className="w-full bg-white border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={regData.address}
                      onChange={handleRegChange}
                      placeholder="e.g. Gangai amman koil street, pangur, thennal post"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">City</label>
                      <input
                        type="text"
                        name="city"
                        value={regData.city}
                        onChange={handleRegChange}
                        placeholder="e.g. PUDUCHERRY"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">State</label>
                      <input
                        type="text"
                        name="state"
                        value={regData.state}
                        onChange={handleRegChange}
                        placeholder="e.g. puducherry"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={regData.pincode}
                        onChange={handleRegChange}
                        placeholder="e.g. 605102"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Website</label>
                      <input
                        type="url"
                        name="website"
                        value={regData.website}
                        onChange={handleRegChange}
                        placeholder="e.g. https://www.smartgonext.com/"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">STD Code (for WhatsApp)</label>
                      <input
                        type="text"
                        name="whatsapp"
                        value={regData.whatsapp}
                        onChange={handleRegChange}
                        placeholder="e.g. +91 9655321915"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">GST Number</label>
                    <input
                      type="text"
                      name="gst"
                      value={regData.gst}
                      onChange={handleRegChange}
                      placeholder="e.g. 36AAAAA1111A1Z1"
                      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Verification Document Upload (Optional)</label>
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files ? e.target.files[0] : null;
                        setRegData(prev => ({
                          ...prev,
                          documentUploaded: file ? file.name : ''
                        }));
                      }}
                      className="w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>

                  <div className="flex gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => setIsRegistering(false)}
                      className="w-1/3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3.5 rounded-xl transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="w-2/3 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md shadow-blue-600/10 border border-blue-500/10"
                    >
                      Next Step <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: CREDENTIALS & PLAN SELECTOR */}
              {regStep === 2 && (
                <div className="space-y-4 animate-fadeIn text-xs">

                  {/* Duplicate Uniqueness Warning Banner */}
                  {userExistsError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex gap-2 items-start text-red-700 animate-pulse">
                      <ShieldAlert className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      <span className="font-bold text-[11px] leading-tight">{userExistsError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Choose Username *</label>
                      <input
                        type="text"
                        name="username"
                        required
                        value={regData.username}
                        onChange={handleRegChange}
                        placeholder="e.g. jdoe"
                        className={`w-full bg-white border rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 ${userExistsError.includes('Username') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                          }`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Secure Password *</label>
                      <input
                        type="password"
                        name="password"
                        required
                        value={regData.password}
                        onChange={handleRegChange}
                        placeholder="••••••••"
                        className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Plan Selector */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Select SaaS Plan *</label>
                    <div className="space-y-2">
                      {apiPlans.length === 0 ? (
                        <div className="text-xs text-slate-400 py-4 text-center">Loading plans...</div>
                      ) : apiPlans.map(planOption => {
                        const isSel = regData.planId === planOption.id;
                        return (
                          <div
                            key={planOption.id}
                            onClick={() => setRegData(prev => ({ ...prev, plan: planOption.name, planId: planOption.id, planData: planOption }))}
                            className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between gap-3 ${isSel
                                ? 'bg-blue-50/50 border-blue-500 shadow-sm'
                                : 'bg-white border-gray-200 hover:border-blue-200'
                              }`}
                          >
                            <div>
                              <div className="font-extrabold text-slate-800 text-[13px]">{planOption.name} Plan</div>
                              <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{planOption.description}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <span className="font-black text-blue-600 text-xs font-mono">
                                ₹{Number(planOption.price).toLocaleString()} / {planOption.cycle === 'Yearly' ? 'yearly' : 'monthly'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Payment Method Selector */}
                  <div className="mt-4">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Select Payment Method *</label>
                    <div className="grid grid-cols-2 gap-3">
                      <div
                        onClick={() => setPaymentMethod('razorpay')}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-center gap-2 font-bold text-xs ${
                          paymentMethod === 'razorpay'
                            ? 'bg-blue-50/50 border-blue-500 text-blue-700 shadow-sm'
                            : 'bg-white border-gray-200 text-slate-650 hover:border-blue-200'
                        }`}
                      >
                        💳 Razorpay
                      </div>
                      <div
                        onClick={() => setPaymentMethod('paypal')}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-center gap-2 font-bold text-xs ${
                          paymentMethod === 'paypal'
                            ? 'bg-blue-50/50 border-blue-500 text-blue-700 shadow-sm'
                            : 'bg-white border-gray-200 text-slate-650 hover:border-blue-200'
                        }`}
                      >
                        🅿️ PayPal
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => setRegStep(1)}
                      className="w-1/3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3.5 rounded-xl transition-all"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!!userExistsError}
                      className={`w-2/3 font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md border ${userExistsError
                          ? 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed shadow-none'
                          : 'bg-blue-600 hover:bg-blue-500 text-white border-blue-500/10 shadow-blue-600/10'
                        }`}
                    >
                      <DollarSign className="w-4 h-4" /> Continue & Pay Now
                    </button>
                  </div>
                </div>
              )}

            </form>

            {/* Back to Login trigger link */}
            <div className="mt-5 text-center text-xs">
              <span className="text-slate-500 font-semibold">Already have an active plan? </span>
              <button
                type="button"
                onClick={() => setIsRegistering(false)}
                className="text-blue-600 hover:text-blue-700 font-bold underline transition-colors"
              >
                Back to Sign In
              </button>
            </div>

          </div>
        )}

        {/* Footer */}
        <div className="text-center text-[10px] text-slate-500 font-medium">
          <p>&copy; 2026 Repair Shop Management System. All rights reserved.</p>
        </div>
      </div>

      {/* REUSABLE SIMULATED RAZORPAY GATEWAY MODAL */}
      <RazorpayModal
        isOpen={isRazorpayOpen}
        onClose={() => setIsRazorpayOpen(false)}
        amountInINR={getPlanPriceINR()}
        description={`SaaS subscription signup - ${regData.plan} Plan`}
        clientName={regData.name}
        clientEmail={regData.email}
        clientPhone={regData.phone}
        onSuccess={handlePaymentSuccess}
      />

      {/* REUSABLE SIMULATED PAYPAL GATEWAY MODAL */}
      <PayPalModal
        isOpen={isPayPalOpen}
        onClose={() => setIsPayPalOpen(false)}
        amountInINR={getPlanPriceINR()}
        description={`SaaS subscription signup - ${regData.plan} Plan`}
        onSuccess={handlePaymentSuccess}
      />

      {/* Modern Public Job Tracking Modal */}
      {showTrackModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col transform scale-100 transition-all overflow-hidden border border-blue-100">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-extrabold">Track Your Repair</h2>
                  <p className="text-xs text-blue-105">Check device status & details instantly</p>
                </div>
              </div>
              <button
                onClick={() => setShowTrackModal(false)}
                className="text-white/85 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all text-xl font-bold line-none flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">

              {/* Search Form */}
              <form onSubmit={handleTrackSubmit} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Phone className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    required
                    className="input-field pl-11 rounded-xl py-3 border-gray-250 focus:border-blue-500 focus:ring-blue-500 font-medium"
                    placeholder="Enter customer phone number"
                    value={trackPhone}
                    onChange={(e) => setTrackPhone(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={trackingLoading}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 shadow-lg shadow-blue-600/10 active:scale-95 transition-all"
                >
                  {trackingLoading ? (
                    <div className="spinner border-white w-5 h-5"></div>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>Search Jobs</span>
                    </>
                  )}
                </button>
              </form>

              {/* Error State */}
              {trackError && (
                <div className="p-4 bg-red-50 border border-red-150 rounded-xl text-red-700 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <span className="text-sm font-semibold">{trackError}</span>
                </div>
              )}

              {/* Tracked Jobs List */}
              {trackedJobs && trackedJobs.length > 0 && (
                <div className="space-y-6">
                  <div className="text-sm font-medium text-slate-500 flex items-center">
                    <ShieldCheck className="w-4 h-4 text-emerald-500 mr-2" />
                    Found {trackedJobs.length} active jobs matching phone number
                  </div>

                  {trackedJobs.map((job) => {
                    const totalPaid = job.payments?.reduce((sum, p) => sum + p.amount, 0) || job.advanceAmount || 0;
                    const grandTotal = job.actualCost || job.estimatedCost || 0;
                    const balanceDue = grandTotal - totalPaid;

                    return (
                      <div key={job.id} className="border border-blue-100 bg-white hover:bg-slate-50/50 rounded-2xl p-5 shadow-sm space-y-4 hover:shadow-md transition-all">

                        {/* Header Details */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-blue-100">
                          <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Job Number</span>
                            <h3 className="text-base font-extrabold text-slate-900">{job.jobNumber}</h3>
                            <div className="flex items-center space-x-1.5 mt-1">
                              <Tag className="w-3.5 h-3.5 text-blue-500" />
                              <span className="text-xs font-bold text-blue-700 uppercase">
                                Store: {job.branch?.name || "Selfie Mobiles"}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5">
                            {getStatusBadge(job.status)}
                          </div>
                        </div>

                        {/* Device Info Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-slate-800">
                          <div className="space-y-1">
                            <span className="text-xs font-semibold text-gray-450">Device Description</span>
                            <div className="font-bold flex items-center text-xs">
                              <Smartphone className="w-4 h-4 text-slate-400 mr-1.5 shrink-0" />
                              {job.brand} {job.modelName}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <span className="text-xs font-semibold text-gray-450">Received Date</span>
                            <div className="font-bold flex items-center text-xs">
                              <Calendar className="w-4 h-4 text-slate-400 mr-1.5 shrink-0" />
                              {job.receivedDate ? new Date(job.receivedDate).toLocaleDateString() : 'Not Specified'}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <span className="text-xs font-semibold text-gray-450">Expected Delivery</span>
                            <div className="font-bold flex items-center text-xs">
                              <Clock className="w-4 h-4 text-slate-400 mr-1.5 shrink-0" />
                              {job.expectedDate || job.expectedDeliveryDate ? new Date(job.expectedDate || job.expectedDeliveryDate).toLocaleDateString() : 'Not Specified'}
                            </div>
                          </div>
                        </div>

                        {/* Customer & Issue Description */}
                        <div className="p-3 bg-blue-50/20 rounded-xl space-y-1 text-xs border border-blue-100">
                          <div>
                            <span className="font-semibold text-slate-650">Customer:</span>{' '}
                            <span className="font-bold text-slate-800 uppercase">{job.customer?.name}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-650">Reported Problem:</span>{' '}
                            <span className="text-slate-700 italic">"{job.problemDescription}"</span>
                          </div>
                        </div>

                        {/* Cost Breakdown Grid */}
                        <div className="grid grid-cols-3 gap-2 bg-gradient-to-r from-blue-50/30 to-indigo-50/30 rounded-xl p-3 border border-blue-100 text-center">
                          <div>
                            <span className="text-xs font-medium text-slate-500 block">Total Amount</span>
                            <span className="text-sm font-bold text-slate-800">₹{grandTotal.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-slate-500 block">Paid</span>
                            <span className="text-sm font-bold text-emerald-600">₹{totalPaid.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-slate-500 block">Balance Due</span>
                            <span className={`text-sm font-black ${balanceDue > 0 ? 'text-red-650' : 'text-emerald-600'}`}>
                              ₹{balanceDue.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Digital Invoice Preview Button */}
                        <div className="flex justify-end pt-1">
                          <button
                            onClick={() => setSelectedInvoiceJob(job)}
                            className="px-4 py-2 bg-white hover:bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold rounded-lg transition-colors flex items-center space-x-1.5 shadow-sm"
                          >
                            <FileText className="w-4 h-4" />
                            <span>View Digital Invoice / Receipt</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-55 px-6 py-4 border-t border-blue-100 flex justify-end">
              <button
                onClick={() => setShowTrackModal(false)}
                className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Receipt Viewer Sub-Modal */}
      {selectedInvoiceJob && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[110] p-4 no-print animate-fade-in font-sans">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-blue-100 text-slate-800">

            {/* Header Controls */}
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between no-print">
              <div>
                <h3 className="font-extrabold text-base">Printable Repair Invoice</h3>
                <p className="text-xs text-slate-400">Order: {selectedInvoiceJob.jobNumber}</p>
              </div>
              <button
                onClick={() => setSelectedInvoiceJob(null)}
                className="text-slate-400 hover:text-white bg-slate-800 p-2 rounded-full transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Printable Invoice Document */}
            <style>{`
              @media print {
                body {
                  background: white !important;
                  color: black !important;
                }
                body * {
                  visibility: hidden !important;
                }
                #customer-digital-invoice, #customer-digital-invoice * {
                  visibility: visible !important;
                }
                #customer-digital-invoice {
                  position: absolute !important;
                  left: 0 !important;
                  top: 0 !important;
                  width: 100% !important;
                  padding: 0 !important;
                  margin: 0 !important;
                  box-shadow: none !important;
                  border: none !important;
                }
              }
            `}</style>
            <div id="customer-digital-invoice" className="p-8 overflow-y-auto flex-1 space-y-6 text-sm text-slate-800 bg-white">

              {/* 3-Column Top Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Column 1: Customer Invoice Info */}
                <div className="border border-blue-100 rounded-xl p-4 bg-blue-50/10 flex flex-col justify-center text-center">
                  <div className="bg-blue-600 text-white font-extrabold text-[11px] uppercase tracking-wider py-1 px-3 rounded-full inline-block mx-auto mb-3">
                    Invoice {selectedInvoiceJob.jobNumber}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block tracking-widest uppercase">DATE</span>
                      <span className="font-extrabold text-slate-900 text-sm">
                        {new Date(selectedInvoiceJob.createdAt).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block tracking-widest uppercase">NAME</span>
                      <span className="font-extrabold text-slate-900 text-sm uppercase">{selectedInvoiceJob.customer?.name}</span>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 block tracking-widest uppercase">PHONE</span>
                      <span className="font-extrabold text-slate-900 text-sm">{selectedInvoiceJob.customer?.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Column 2: Barcode & Tracking */}
                <div className="border border-blue-100 rounded-xl p-4 bg-blue-50/10 flex flex-col items-center justify-center text-center">
                  <span className="text-[10px] font-bold text-slate-550 tracking-wider mb-1 block">BAR CODE MANAGEMENT</span>

                  {/* CSS Styled Pure Barcode */}
                  <div className="flex justify-center items-center h-8 my-2 space-x-[2px] bg-white p-1 border rounded border-blue-100">
                    <span className="w-[2px] h-6 bg-black"></span>
                    <span className="w-[1px] h-6 bg-black"></span>
                    <span className="w-[3px] h-6 bg-black"></span>
                    <span className="w-[1px] h-6 bg-black"></span>
                    <span className="w-[2px] h-6 bg-black"></span>
                    <span className="w-[4px] h-6 bg-black"></span>
                    <span className="w-[1px] h-6 bg-black"></span>
                    <span className="w-[2px] h-6 bg-black"></span>
                  </div>

                  {/* QR Code Graphic Icon */}
                  <div className="w-16 h-16 bg-white p-1 border border-blue-100 rounded-lg shadow-sm flex items-center justify-center my-1.5 overflow-hidden">
                    {loginQrCodeUrl ? (
                      <img
                        src={loginQrCodeUrl}
                        alt="QR Code"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="spinner w-4 h-4"></div>
                    )}
                  </div>

                  <span className="text-[9px] text-slate-450 mt-1 leading-tight">
                    Check online at<br />
                    <span className="text-blue-600 underline font-semibold">
                      {selectedInvoiceJob.customer?.phone ? `${window.location.origin.replace(/^https?:\/\//, '')}/login?phone=${selectedInvoiceJob.customer.phone}` : `${window.location.origin.replace(/^https?:\/\//, '')}/login`}
                    </span>
                  </span>
                </div>

                {/* Column 3: Store Details */}
                <div className="border border-blue-100 rounded-xl p-4 bg-blue-50/10 flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-blue-100 p-1 flex items-center justify-center mb-2 overflow-hidden">
                    <img
                      src={(selectedInvoiceJob.companySettings?.logo || selectedInvoiceJob.branch?.logo) ? ((selectedInvoiceJob.companySettings?.logo || selectedInvoiceJob.branch?.logo).startsWith('http') ? (selectedInvoiceJob.companySettings?.logo || selectedInvoiceJob.branch?.logo) : `${api.defaults.baseURL || ''}${selectedInvoiceJob.companySettings?.logo || selectedInvoiceJob.branch?.logo}`) : "https://cdn-icons-png.flaticon.com/512/2942/2942544.png"}
                      alt="Store Logo"
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/2942/2942544.png"; }}
                    />
                  </div>
                  <h4 className="font-black text-slate-900 text-sm uppercase tracking-wide">
                    {selectedInvoiceJob.branch?.name || selectedInvoiceJob.companySettings?.companyName || "Selfie Mobiles"}
                  </h4>
                  <p className="text-[10px] text-slate-500 mt-1 font-medium max-w-[180px] leading-tight">
                    {selectedInvoiceJob.branch?.address || selectedInvoiceJob.companySettings?.address || "125B, KAMARAJ STREET, RAJAPALAYAM-626117"}
                  </p>
                  <p className="text-[10px] text-slate-600 font-bold mt-1.5">
                    Phone: {selectedInvoiceJob.branch?.phone || selectedInvoiceJob.companySettings?.phone || "8838429268"}
                  </p>
                </div>

              </div>

              {/* Invoice Main Repair Item Table */}
              <div className="overflow-x-auto border border-blue-100 rounded-xl shadow-sm bg-white">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-blue-50/20 border-b border-blue-100 text-slate-650 text-xs font-bold uppercase">
                      <th className="px-4 py-3 text-center w-12">At</th>
                      <th className="px-4 py-3">Repair item</th>
                      <th className="px-4 py-3">Defect</th>
                      <th className="px-4 py-3">Repair details</th>
                      <th className="px-4 py-3 text-right">Repair Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50 text-xs font-medium text-slate-800">
                    <tr className="hover:bg-slate-50/50">
                      <td className="px-4 py-4 text-center text-slate-400 font-bold">1</td>
                      <td className="px-4 py-4 font-black uppercase text-slate-905">
                        {selectedInvoiceJob.brand} {selectedInvoiceJob.modelName}
                      </td>
                      <td className="px-4 py-4 text-slate-650">
                        {selectedInvoiceJob.problemDescription || "Device Diagnostics"}
                      </td>
                      <td className="px-4 py-4 text-slate-500 italic">
                        {selectedInvoiceJob.notes || "Standard hardware repair service"}
                      </td>
                      <td className="px-4 py-4 text-right font-bold text-slate-900">
                        rupees {(selectedInvoiceJob.actualCost ? (selectedInvoiceJob.actualCost - (selectedInvoiceJob.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0) - selectedInvoiceJob.labourCost) : selectedInvoiceJob.estimatedCost || 0).toFixed(2)}
                      </td>
                    </tr>

                    {/* Render any additional spare parts / inventory job items */}
                    {selectedInvoiceJob.jobItems && selectedInvoiceJob.jobItems.map((item, idx) => (
                      <tr key={item.id} className="hover:bg-slate-50/50 text-[11px]">
                        <td className="px-4 py-3 text-center text-slate-400 font-bold">{idx + 2}</td>
                        <td className="px-4 py-3 font-semibold text-gray-700">
                          Spare: {item.product.name}
                        </td>
                        <td className="px-4 py-3 text-gray-500">Inventory Part Charge</td>
                        <td className="px-4 py-3 text-gray-400">Qty: {item.quantity} @ rupees {item.rate.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-700">
                          rupees {item.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))}

                    {/* Render Labour Charges if any */}
                    {selectedInvoiceJob.labourCost > 0 && (
                      <tr className="hover:bg-slate-50/50 text-[11px]">
                        <td className="px-4 py-3 text-center text-slate-400 font-bold">
                          {selectedInvoiceJob.jobItems ? selectedInvoiceJob.jobItems.length + 2 : 2}
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-700">
                          Labour Charges
                        </td>
                        <td className="px-4 py-3 text-slate-500">Service Fee</td>
                        <td className="px-4 py-3 text-slate-450">Standard service charge</td>
                        <td className="px-4 py-3 text-right font-bold text-slate-700">
                          rupees {selectedInvoiceJob.labourCost.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    <tr className="h-6">
                      <td colSpan="5"></td>
                    </tr>

                    {/* Total Cost, Paid, and Balance Summary Rows */}
                    <tr className="border-t border-blue-100">
                      <td colSpan="3" className="px-4 py-2"></td>
                      <td className="px-4 py-2 text-right text-xs font-semibold uppercase text-slate-505 tracking-wider">
                        Total Amount
                      </td>
                      <td className="px-4 py-2 text-right text-xs font-bold text-slate-950">
                        rupees {(selectedInvoiceJob.actualCost || (selectedInvoiceJob.estimatedCost + (selectedInvoiceJob.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0) + selectedInvoiceJob.labourCost)).toFixed(2)}
                      </td>
                    </tr>

                    <tr>
                      <td colSpan="3" className="px-4 py-1"></td>
                      <td className="px-4 py-1 text-right text-xs font-semibold uppercase text-slate-505 tracking-wider">
                        Total Paid / Advance
                      </td>
                      <td className="px-4 py-1 text-right text-xs font-bold text-green-600">
                        rupees {(selectedInvoiceJob.payments?.reduce((sum, p) => sum + p.amount, 0) || selectedInvoiceJob.advanceAmount || 0).toFixed(2)}
                      </td>
                    </tr>

                    <tr className="bg-blue-50/10 border-t-2 border-blue-100">
                      <td colSpan="3" className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right text-xs font-extrabold uppercase text-slate-550 tracking-wider">
                        Balance Due
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-black text-slate-950 bg-blue-50/30">
                        rupees {((selectedInvoiceJob.actualCost || (selectedInvoiceJob.estimatedCost + (selectedInvoiceJob.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0) + selectedInvoiceJob.labourCost)) -
                          (selectedInvoiceJob.payments?.reduce((sum, p) => sum + p.amount, 0) || selectedInvoiceJob.advanceAmount || 0)).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Bottom Section: Terms & Signature Grid */}
              <div className="space-y-6 pt-2">

                {/* Terms and Conditions */}
                <div className="text-xs space-y-1 bg-blue-50/10 p-4 border border-blue-100 rounded-xl">
                  <h5 className="font-extrabold text-[11px] text-slate-800 uppercase tracking-wider mb-2">TERMS AND CONDITIONS*</h5>
                  <ol className="list-none space-y-1.5 text-slate-650 leading-normal pl-0">
                    {(() => {
                      if (selectedInvoiceJob.companySettings?.terms) {
                        try {
                          const parsed = JSON.parse(selectedInvoiceJob.companySettings.terms);
                          if (Array.isArray(parsed) && parsed.length > 0) {
                            return parsed.map((term, i) => (
                              <li key={i}>{i + 1}-{term}</li>
                            ));
                          }
                        } catch (err) { }
                      }
                      // Fallback to defaults
                      return (
                        <>
                          <li>1-We will not be held Liable for any loss of Data in case of software problem</li>
                          <li>2-Collect Your Mobile within 15 days after complete your device repair</li>
                        </>
                      );
                    })()}
                  </ol>
                </div>

                {/* Signature Box */}
                <div className="grid grid-cols-2 gap-4 border border-blue-100 rounded-xl p-5 bg-blue-50/5 text-center">
                  <div className="space-y-8 flex flex-col justify-between">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">sign recipient</span>
                    <div className="border-b border-dashed border-gray-300 w-2/3 mx-auto"></div>
                  </div>
                  <div className="space-y-8 flex flex-col justify-between">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">sign client</span>
                    <div className="border-b border-dashed border-gray-300 w-2/3 mx-auto"></div>
                  </div>
                </div>

                <p className="text-[10px] font-bold text-slate-450 text-center uppercase tracking-wide pt-2">
                  Customer Signature (Please sign below)
                </p>

              </div>

            </div>

            {/* Footer Control Panel */}
            <div className="bg-slate-50 p-4 border-t border-blue-100 flex justify-end space-x-2 no-print">
              <button
                onClick={() => setSelectedInvoiceJob(null)}
                className="px-5 py-2.5 bg-slate-200 hover:bg-slate-350 text-slate-700 font-bold rounded-xl text-xs transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => window.print()}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs transition-colors flex items-center space-x-1.5 shadow-sm shadow-blue-600/10 active:scale-95 animate-pulse"
              >
                <Download className="w-4 h-4" />
                <span>Print / Save Receipt</span>
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Login;